'use client';

import type { FC } from 'react';
import React, { useEffect, useState } from 'react';

import type { OrderType } from '@hyex/dxp/types/domain/OrderType';
import { convertPrice } from '@hyex/modules/portal/domain/domain.utils';
import type { ProductVariantType } from '@hyex/dxp/types/domain/ProductVariantType';

type PropTypes = {
  options: ProductVariantType[];
  orderTypes: OrderType[];
  setSelectedSku: (option: ProductVariantType) => void;
  setSelectedIndex: (index: number) => void;
  defaultSelectedIndex?: number;
  ignoreThisIndex: number;
};

export const ProductVariants: FC<PropTypes> = ({
  options,
  orderTypes,
  setSelectedSku,
  setSelectedIndex,
  defaultSelectedIndex,
}) => {
  const [selectedOption, setSelectedOption] = useState(
    options.length > (defaultSelectedIndex ?? 0) ? options[defaultSelectedIndex ?? 0] : options[0]
  );

  const onOptionClicked = (value: any, index: number) => () => {
    setSelectedOption(value);

    // setSelectedSku(value);

    setSelectedIndex(index);
  };

  useEffect(() => {
    setSelectedOption(options[defaultSelectedIndex ?? 0]);
  }, [defaultSelectedIndex, options]);

  const price = (price: string) => {
    return convertPrice(price);
  };

  const savePriceText = (price: string, numberOfBottles: number) => {
    if (!price || !numberOfBottles || numberOfBottles === 1) return '';

    const oneBottlePriceStr = getOneBottlePrice();

    const oneBottlePrice = oneBottlePriceStr ? convertPrice(oneBottlePriceStr) : 0;

    const totalRegularPrice = oneBottlePrice ? oneBottlePrice * numberOfBottles : 0;

    const priceDiff = totalRegularPrice - convertPrice(price);

    return ' - Save $' + priceDiff.toFixed(0);
  };

  const getNumberOfBottles = (sku: string) => {
    const skuNumber = sku.match(/\d+/g) ?? [''];
    const skuNumberString = skuNumber.join('');

    return skuNumberString;
  };

  const getOneBottlePrice = () => {
    const variant = options.find((o) => o.skuId.indexOf('1') === 0);
    return variant?.price;
  };

  const getOrderType = (sku: string) => {
    const skuNumberString = getNumberOfBottles(sku);

    const orderType = orderTypes.find((ot) => ot.quantity === Number(skuNumberString));

    return orderType;
  };

  return (
    <div className="radio-wrapper">
      {options.map((option, i) => {
        const orderType = getOrderType(option.skuId);
        const pricePerBottle = orderType
          ? price(option.price) / orderType.quantity
          : price(option.price);

        return (
          orderType && (
            <div className={orderType.className} key={orderType.id}>
              <label
                className={`radio-label ${option.skuId === selectedOption.skuId ? 'active' : ''}`}
              >
                <input
                  id={option.skuId}
                  type="radio"
                  name={option.skuId}
                  className={`radio-input `}
                  value={option.skuId}
                  checked={option.skuId === selectedOption.skuId}
                  onChange={onOptionClicked(option, i)}
                />

                <span className="option-text">{orderType.text}</span>
                <div
                  className="option-list"
                  dangerouslySetInnerHTML={{ __html: orderType.html }}
                ></div>
                <span className="option-price">
                  ${pricePerBottle.toFixed(2)}
                  <span className="per-bottle">per bottle</span>
                  {option.oldPrice && (
                    <span className="original-price">
                      Original <s>${price(option.oldPrice).toFixed(2)}</s>
                    </span>
                  )}
                </span>
              </label>
              <span className="best-value">
                {orderType.title}
                {savePriceText(option.price, orderType.quantity)}
              </span>
            </div>
          )
        );
      })}
    </div>
  );
};

export default ProductVariants;
